<template>
  <div class="pricing-page">
    <header class="pricing-top">
      <BackToSiteButton @click="leave" />
    </header>

    <main class="pricing-content">
      <div class="pricing-hero">
        <h1 class="pricing-title">{{ $t('premium.title') }}</h1>

        <div class="payment-options">
          <div class="period-selection">
            <div class="toggle-buttons purchase-mode" role="group" :aria-label="$t('premium.purchaseMode.label')">
              <button
                type="button"
                :class="{ active: purchaseMode === 'subscription' }"
                @click="purchaseMode = 'subscription'"
              >
                {{ $t('premium.purchaseMode.subscription') }}
              </button>
              <button
                type="button"
                :class="{ active: purchaseMode === 'credits' }"
                @click="purchaseMode = 'credits'"
              >
                {{ $t('premium.purchaseMode.credits') }}
              </button>
            </div>
            <div
              v-if="purchaseMode === 'subscription'"
              class="toggle-buttons billing-period"
              role="group"
              :aria-label="$t('premium.billingPeriod')"
            >
              <button
                type="button"
                :class="{ active: billingPeriod === 'monthly' }"
                @click="billingPeriod = 'monthly'"
              >
                {{ $t('premium.monthly') }}
              </button>
              <button
                type="button"
                :class="{ active: billingPeriod === 'yearly' }"
                @click="billingPeriod = 'yearly'"
              >
                {{ $t('premium.yearly') }}
              </button>
            </div>
            <div class="payment-note">
              <CircleCheckFilled aria-hidden="true" />
              {{ purchaseMode === 'subscription'
                ? $t('premium.subscriptionNote')
                : $t('premium.creditPackNote') }}
            </div>
          </div>
        </div>
        <!-- 只有中文界面有：订阅走不了支付宝，单买额度按纽币结账 -->
        <p
          v-if="$te(`premium.paymentMethodNote.${purchaseMode}`)"
          class="payment-method-note"
          :class="purchaseMode"
        >
          <InfoFilled aria-hidden="true" />
          {{ $t(`premium.paymentMethodNote.${purchaseMode}`) }}
        </p>
      </div>

      <p v-if="error" class="premium-error" role="alert">{{ error }}</p>

      <div
        v-if="purchaseMode === 'subscription' && hasActiveSubscription"
        class="active-subscription"
      >
        <div>
          <strong>{{ $t('premium.activeSubscription.title') }}</strong>
          <p>{{ $t(canManageSubscription
            ? 'premium.activeSubscription.description'
            : 'premium.activeSubscription.grantedDescription') }}</p>
        </div>
        <button
          v-if="canManageSubscription"
          type="button"
          :disabled="loading"
          @click="handleBilling"
        >
          {{ $t('userMenu.manageBilling') }}
        </button>
      </div>
      
      <div class="plans-container" role="list">
        <article
          v-for="plan in displayCards"
          :key="plan.id"
          class="plan-card"
          :class="[plan.id, { 'credit-pack': purchaseMode === 'credits' }]"
          role="listitem"
        >
          <div class="plan-header">
            <h3>{{ plan.title }}</h3>
            <p class="plan-description">{{ plan.description }}</p>
            <div class="price-container">
              <div class="price">
                <span class="price-amount">{{ plan.priceParts.amount }}</span>
                <span v-if="plan.priceParts.period" class="price-period">{{ plan.priceParts.period }}</span>
              </div>
            </div>
            <div class="discount-slot">
              <s v-if="plan.referenceAmount" class="price-reference">{{ plan.referenceAmount }}</s>
              <span v-if="plan.discount" class="discount" :class="billingPeriod">
                {{ plan.discount }}
              </span>
            </div>
          </div>

          <dl class="features-list">
            <div
              v-for="feature in plan.features"
              :key="feature.key"
              class="feature-item"
            >
              <dt>{{ feature.label }}</dt>
              <dd>{{ feature.value }}</dd>
            </div>
          </dl>

          <button
            v-if="plan.action"
            class="upgrade-button"
            type="button"
            @click="handlePurchase(plan.action)"
            :disabled="loading"
          >
            <span>{{ plan.cta }}</span>
            <Right aria-hidden="true" />
          </button>
          <div v-else class="upgrade-button static-button" aria-disabled="true">
            <CircleCheckFilled aria-hidden="true" />
            <span>{{ plan.cta }}</span>
          </div>
        </article>
      </div>
      <p class="plan-allowance-note">
        {{ purchaseMode === 'subscription'
          ? $t('premium.allowanceNote')
          : $t('premium.creditPackAllowanceNote') }}
        {{ referenceNote }}
      </p>
    </main>
  </div>
</template>

<script>
import { auth } from '@/firebase'
import { apiPost } from '@/composables/useApi'
import { useAuth } from '@/composables/auth'
import { getPricingReturnPath, useModals } from '@/composables/modals'
import BackToSiteButton from '@/components/common/BackToSiteButton.vue'
import {
  CircleCheckFilled,
  InfoFilled,
  Right
} from '@element-plus/icons-vue'

const specKeys = [
  'credits',
  'pronunciation',
  'transcription',
  'tts',
  'upload',
  'text',
  'priority'
]

export default {
  name: 'PricingPage',
  components: {
    BackToSiteButton,
    CircleCheckFilled,
    InfoFilled,
    Right
  },
  props: {
    userDetails: {
      type: Object,
      default: () => ({})
    }
  },
  setup() {
    const { showLoginModal } = useModals()
    const { user } = useAuth()
    return { showLoginModal, user }
  },
  data() {
    return {
      // /pricing?mode=credits 直接打开"单买额度"
      purchaseMode: this.$route.query.mode === 'credits' ? 'credits' : 'subscription',
      billingPeriod: 'monthly',
      loading: false,
      error: null,
      // 首次购买优惠：{ subscription: { percentOff, months }, credits: { percentOff } }，没有的那项不出现
      firstPurchaseOffer: {}
    }
  },
  computed: {
    currentPrice() {
      const key = `${this.billingPeriod}Subscription`
      return this.$t(`premium.premiumPlan.${key}`)
    },
    currentProPrice() {
      const key = `${this.billingPeriod}Subscription`
      return this.$t(`premium.proPlan.${key}`)
    },
    currentPriceParts() {
      return this.formatPrice(this.currentPrice)
    },
    currentProPriceParts() {
      return this.formatPrice(this.currentProPrice)
    },
    currentFreePriceParts() {
      return this.formatPrice(this.$t('premium.freePlan.price'))
    },
    planId() {
      return `${this.billingPeriod}_autoRenewal`
    },
    activePlanId() {
      if (this.userDetails?.isPremium) return 'premium'
      if (this.userDetails?.isPro && !this.userDetails?.hasPurchasedCredits) return 'pro'
      return 'free'
    },
    hasActiveSubscription() {
      return this.activePlanId === 'pro' || this.activePlanId === 'premium'
    },
    canManageSubscription() {
      return Boolean(this.userDetails?.canManageSubscription)
    },
    specRows() {
      return specKeys.map((key) => ({
        key,
        label: this.$t(`premium.featureLabels.${key}`)
      }))
    },
    planCards() {
      // 首次购买优惠只给月付
      const offer = this.billingPeriod === 'monthly' ? this.firstPurchaseOffer.subscription : null
      const buildPlan = ({ id, planKey, priceParts, action }) => ({
        id,
        title: this.$t(`premium.${planKey}.title`),
        description: this.$t(`premium.${planKey}.description`),
        priceParts: action && offer
          ? { ...priceParts, amount: this.discountedAmount(priceParts.amount, offer) }
          : priceParts,
        // 年付的划线价：同一方案按月付 12 个月要花的钱；首次优惠的划线价是原价
        referenceAmount: action && this.billingPeriod === 'yearly'
          ? this.withAmount(priceParts.amount, this.amountOf(this.$t(`premium.${planKey}.monthlySubscription`)) * 12)
          : action && offer
            ? priceParts.amount
            : '',
        discount: action && this.billingPeriod !== 'monthly'
          ? this.$t(`premium.${planKey}.${this.billingPeriod}Discount`)
          : action && offer
            ? this.$t('premium.firstPurchase.subscriptionBadge', this.offerLabels(offer))
            : '',
        cta: id === this.activePlanId
          ? this.$t('premium.cta.current')
          : id === 'free' && this.hasActiveSubscription
            ? this.$t('premium.cta.included')
          : this.hasActiveSubscription && id !== 'free'
            ? this.$t(this.canManageSubscription
              ? 'premium.cta.manageExisting'
              : 'premium.cta.activePlan')
            : action
              ? this.$t(`premium.cta.${id}`)
              : this.$t('premium.cta.current'),
        action: this.hasActiveSubscription && id !== 'free' ? null : action,
        features: this.specRows.map((row) => ({
          ...row,
          value: this.$t(`premium.${planKey}.specs.${row.key}`)
        }))
      })

      return [
        buildPlan({
          id: 'free',
          planKey: 'freePlan',
          priceParts: this.currentFreePriceParts,
          action: null
        }),
        buildPlan({
          id: 'pro',
          planKey: 'proPlan',
          priceParts: this.currentProPriceParts,
          action: {
            planType: 'pro',
            planId: this.planId
          }
        }),
        buildPlan({
          id: 'premium',
          planKey: 'premiumPlan',
          priceParts: this.currentPriceParts,
          action: {
            planType: 'premium',
            planId: this.planId
          }
        })
      ]
    },
    creditPackCards() {
      // 大额度包的划线价：同样多的额度按 2,000 包的单价要花的钱
      const baseRate = this.amountOf(this.$t('premium.creditPacks.2000.price')) / 2000
      const offer = this.firstPurchaseOffer.credits
      return ['2000', '5000', '10000'].map((amount) => ({
        id: `credits-${amount}`,
        title: this.$t(`premium.creditPacks.${amount}.title`),
        description: this.$t(`premium.creditPacks.${amount}.description`),
        priceParts: this.formatPrice(offer
          ? this.discountedAmount(this.$t(`premium.creditPacks.${amount}.price`), offer)
          : this.$t(`premium.creditPacks.${amount}.price`)),
        referenceAmount: offer
          ? this.$t(`premium.creditPacks.${amount}.price`)
          : amount === '2000'
            ? ''
            : this.withAmount(this.$t(`premium.creditPacks.${amount}.price`), baseRate * Number(amount)),
        discount: offer
          ? this.$t('premium.firstPurchase.creditsBadge', this.offerLabels(offer))
          : amount === '10000'
            ? this.$t('premium.creditPacks.bestValue')
            : '',
        cta: this.$t('premium.creditPacks.cta'),
        action: {
          planType: 'credits',
          planId: `credits_${amount}`
        },
        features: ['balance', 'access', 'expiry'].map((key) => ({
          key,
          label: this.$t(`premium.creditPacks.featureLabels.${key}`),
          value: this.$t(`premium.creditPacks.${amount}.features.${key}`)
        }))
      }))
    },
    displayCards() {
      return this.purchaseMode === 'subscription'
        ? this.planCards
        : this.creditPackCards
    },
    referenceNote() {
      const { subscription, credits } = this.firstPurchaseOffer
      if (this.purchaseMode === 'credits') {
        return credits
          ? this.$t('premium.firstPurchase.creditsNote')
          : this.$t('premium.referenceNote.credits')
      }
      if (this.billingPeriod === 'yearly') return this.$t('premium.referenceNote.yearly')
      return subscription
        ? this.$t('premium.firstPurchase.subscriptionNote', this.offerLabels(subscription))
        : ''
    }
  },
  watch: {
    // 登录或换账号后，优惠资格要重新问一次
    'user.uid': {
      immediate: true,
      handler() {
        this.loadFirstPurchaseOffer()
      }
    }
  },
  methods: {
    async loadFirstPurchaseOffer() {
      // 等登录状态恢复后再问：刷新页面时会先经过一小段"未登录"，那时问到的是访客的优惠
      await auth.authStateReady()
      const uid = auth.currentUser?.uid
      const { offer } = await apiPost('/api/first-purchase-offer', {}, { auth: Boolean(uid) })
      // 期间换了账号的话，这次的结果已经不对应当前用户
      if (auth.currentUser?.uid === uid) this.firstPurchaseOffer = offer
    },
    discountedAmount(label, offer) {
      return this.withAmount(label, this.amountOf(label) * (100 - offer.percentOff) / 100)
    },
    // 文案里用的数字：40% 的折扣在中文里写成"6 折"
    offerLabels({ percentOff, months }) {
      return { percent: percentOff, fold: (100 - percentOff) / 10, months, nextMonth: months + 1 }
    },
    leave() {
      return this.$router.replace(getPricingReturnPath())
    },
    // 价格文案形如 "US$8.99 / 月"：取出数字，或者换成另一个数字
    amountOf(label) {
      return Number(/[\d.]+/.exec(label)[0])
    },
    withAmount(label, value) {
      return label.replace(/[\d.]+/, value.toFixed(2))
    },
    formatPrice(price) {
      const [amount, period] = String(price).split(/\s*\/\s*/)
      return {
        amount,
        period: period ? `/${period}` : ''
      }
    },
    async handlePurchase(selection) {
      try {
        this.loading = true
        this.error = null

        const user = auth.currentUser
        if (!user) {
          this.showLoginModal()
          return
        }

        const { url } = await apiPost('/api/create-checkout-session', {
          planId: selection.planId,
          planType: selection.planType,
          locale: this.$i18n.locale
        })
        window.location.href = url
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    async handleBilling() {
      try {
        this.loading = true
        this.error = null
        const { url } = await apiPost('/api/create-billing-portal-session', {})
        window.location.href = url
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* 整页盖在站点之上：底下的工作区保持原样，返回或付款后回到原处 */
.pricing-page {
  position: fixed;
  inset: 0;
  z-index: 1000;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--color-page);
}

.pricing-top {
  padding: var(--space-6) var(--space-10) 0;
}

.pricing-content {
  width: min(1120px, 100%);
  margin: 0 auto;
  padding: var(--space-8) var(--space-6) var(--space-16);
}

.pricing-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 1.25rem;
  margin-bottom: var(--space-6);
}

.pricing-title {
  margin: 0;
  font-size: clamp(1.7rem, 2.5vw, 2.2rem);
  line-height: 1.12;
  letter-spacing: -0.025em;
  color: var(--color-text);
  font-family: var(--font-family-display);
  font-weight: 600;
}

.payment-options {
  min-width: 0;
  display: flex;
  justify-content: flex-end;
}

.toggle-buttons {
  display: inline-flex;
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 4px;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
}

.toggle-buttons button {
  min-height: 34px;
  padding: 0 16px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 9px;
  font-weight: 700;
  color: var(--color-text-muted);
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
  font-size: 0.9rem;
}

.toggle-buttons button.active {
  background: var(--surface-panel);
  color: var(--color-text);
  box-shadow: var(--shadow-sm);
}

.period-selection {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.payment-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-muted);
  font-size: 0.9rem;
  padding: 0.54rem 0.78rem;
  border: 1px solid var(--border-subtle);
  background: var(--surface-subtle);
  border-radius: 12px;
  white-space: nowrap;
}

.payment-note svg {
  width: 1rem;
  height: 1rem;
  color: var(--color-brand-500);
}

.payment-method-note {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.5;
}

.payment-method-note svg {
  flex: none;
  width: 1.1rem;
  height: 1.1rem;
}

/* 订阅不能用支付宝，是限制，用提醒色 */
.payment-method-note.subscription {
  color: var(--color-warning-text);
  background: var(--color-warning-bg);
  border: 1px solid rgba(217, 119, 6, 0.28);
}

.payment-method-note.credits {
  color: var(--color-brand-700);
  background: var(--surface-active);
  border: 1px solid rgba(8, 125, 114, 0.24);
}

.premium-error {
  margin: 0 0 1rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(220, 38, 38, 0.18);
  border-radius: 12px;
  color: #991b1b;
  background: rgba(254, 242, 242, 0.86);
  font-size: 0.92rem;
}

.active-subscription {
  margin: 0 0 1rem;
  padding: 0.9rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid var(--border-active);
  border-radius: 12px;
  background: var(--surface-active);
}

.active-subscription strong {
  color: var(--color-text);
  font-size: 0.92rem;
}

.active-subscription p {
  margin: 0.2rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.active-subscription button {
  flex: 0 0 auto;
  min-height: 38px;
  padding: 0 0.9rem;
  border: 1px solid var(--border-active);
  border-radius: 10px;
  color: var(--color-brand-800);
  background: var(--surface-panel);
  font-weight: 750;
  cursor: pointer;
}

.plans-container {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  border: 1px solid var(--border-default);
  border-radius: 18px;
  overflow: hidden;
}

.plan-allowance-note {
  margin: 0.75rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.78rem;
  line-height: 1.45;
  text-align: center;
}

.plan-card {
  min-width: 0;
  padding: 1.25rem 1.35rem;
  border: 0;
  border-radius: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  background: var(--surface-panel);
  box-shadow: none;
}

.plan-card.free {
  background: var(--surface-subtle);
}

.plan-card.pro,
.plan-card.premium {
  color: var(--color-text);
  position: relative;
  overflow: hidden;
  border-left: 1px solid rgba(17, 24, 39, 0.08);
}

.plan-card.credit-pack {
  position: relative;
  background: linear-gradient(180deg, #fff 0%, rgba(236, 253, 245, 0.32) 100%);
}

.plan-card.credit-pack + .plan-card.credit-pack {
  border-left: 1px solid rgba(17, 24, 39, 0.08);
}

.plan-card.credit-pack .price {
  background: linear-gradient(135deg, var(--color-brand-900) 0%, var(--color-brand-600) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.plan-card.credit-pack .features-list {
  grid-template-rows: repeat(3, 58px);
}

.plan-card.credit-pack .feature-item {
  min-height: 58px;
}

.plan-card.credit-pack .upgrade-button {
  background: var(--color-brand-900);
  color: var(--color-text-inverse);
  border-color: var(--color-brand-900);
}

.plan-card.pro {
  background: var(--surface-active);
}

.plan-card.premium {
  background: var(--surface-panel);
}

.plan-card.pro .price,
.plan-card.premium .price {
  background: linear-gradient(135deg, var(--color-brand-900) 0%, var(--color-brand-600) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.plan-card.pro .discount,
.plan-card.premium .discount {
  background: var(--color-brand-100);
  color: var(--color-brand-800);
}

.plan-card.pro .upgrade-button,
.plan-card.premium .upgrade-button {
  background: var(--color-brand-900);
  color: var(--color-text-inverse);
  border-color: var(--color-brand-900);
}

.plan-header {
  text-align: left;
  min-height: 144px;
  flex: 0 0 auto;
  display: grid;
  align-content: start;
  grid-template-rows: auto auto auto 26px;
}

.plan-header h3 {
  margin: 0;
  font-size: 1.18rem;
  line-height: 1.2;
}

.plan-description {
  margin: 0.45rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.95rem;
  opacity: 0.9;
}

.features-list {
  flex: 0 0 auto;
  display: grid;
  grid-template-rows: repeat(7, 44px);
  gap: 0;
  border-top: 1px solid rgba(17, 24, 39, 0.08);
  margin: 0 0 0.25rem;
  padding: 0;
}

.feature-item {
  min-height: 44px;
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  padding: 0;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
}

.feature-item dt {
  color: var(--color-text-muted);
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
}

.feature-item dd {
  min-width: 0;
  margin: 0;
  color: var(--color-text);
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: right;
  overflow-wrap: anywhere;
}

.upgrade-button {
  margin-top: auto;
  width: 100%;
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  background: var(--color-surface);
  color: var(--color-brand-800);
  border: 1px solid rgba(23, 107, 77, 0.16);
  padding: 0 1rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  position: relative;
  flex: 0 0 auto;
}

.upgrade-button svg {
  width: 1.05rem;
  height: 1.05rem;
  flex: 0 0 auto;
}

.upgrade-button:hover:not(:disabled) {
  transform: none;
  box-shadow: none;
}

.upgrade-button:active:not(:disabled) {
  transform: translateY(0);
}

.static-button {
  cursor: default;
  color: var(--color-text-muted);
  background: var(--surface-subtle);
  border-color: rgba(17, 24, 39, 0.08);
}

.upgrade-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.price-container {
  min-height: 50px;
  margin: 0.9rem 0 0;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.price {
  max-width: 100%;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.34rem;
  font-weight: 800;
  color: var(--color-text);
  line-height: 1.02;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-text-muted) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.price-reference {
  color: var(--ink-tertiary);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.price-amount {
  font-size: clamp(1.9rem, 3vw, 2.42rem);
  letter-spacing: -0.04em;
}

.price-period {
  font-size: clamp(0.98rem, 1.5vw, 1.18rem);
  letter-spacing: -0.02em;
}

.discount-slot {
  min-height: 26px;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.discount {
  font-size: 0.78rem;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  font-weight: 760;
  white-space: nowrap;
}

.discount.quarterly {
  background: var(--color-brand-50);
}

.discount.halfYearly {
  background: var(--color-brand-100);
}

.discount.yearly {
  background: var(--color-brand-200);
}

@media (max-width: 980px) {
  .plans-container {
    grid-template-columns: 1fr;
  }

  .plan-card.pro,
  .plan-card.premium {
    border-left: 0;
    border-top: 1px solid rgba(17, 24, 39, 0.08);
  }

  .plan-card.credit-pack + .plan-card.credit-pack {
    border-left: 0;
    border-top: 1px solid rgba(17, 24, 39, 0.08);
  }
}

@media (max-width: 768px) {
  .pricing-top {
    padding: var(--space-4) var(--space-5) 0;
  }

  .pricing-content {
    padding: var(--space-5) var(--space-4) var(--space-10);
  }

  .pricing-hero {
    grid-template-columns: 1fr;
    gap: 1rem;
    align-items: start;
  }

  .payment-options,
  .period-selection {
    justify-content: stretch;
  }

  .payment-method-note {
    align-items: flex-start;
  }

  .payment-method-note svg {
    margin-top: 0.2rem;
  }

  .plan-card {
    padding: 1rem;
  }

  .plan-header {
    min-height: auto;
    grid-template-rows: auto;
  }

  .features-list {
    grid-template-rows: repeat(7, minmax(42px, auto));
  }

  .discount {
    font-size: 0.8rem;
    padding: 3px 10px;
  }

  .toggle-buttons {
    width: 100%;
    justify-content: center;
  }
  
  .toggle-buttons button {
    flex: 1 1 calc(50% - 4px);
    text-align: center;
    padding: 10px 12px;
  }

  .payment-note {
    width: 100%;
    justify-content: center;
    white-space: normal;
    text-align: center;
  }
}
</style>
